/**
 * DashboardSyncStatus — silent background sync (Phase 15.1, revised 1.67.x).
 *
 * Background uploads are invisible. Nothing is rendered while work is saving,
 * queued, retrying, prefetching or offline, and nothing is rendered for
 * internal exceptions (authorisation/RLS denials, verification failures,
 * corrupt durable bytes). Those remain fully observable in telemetry and
 * support tooling — they are simply not the driver's problem to solve.
 *
 * The only thing shown here is a genuinely USER-ACTIONABLE problem: currently
 * the device being out of storage, which the person holding the phone can fix.
 *
 * Observation only — the upload engine, retry logic and offline architecture
 * are untouched.
 */
import { usePendingUploads } from "@/hooks/usePendingUploads";
import { ChevronRight, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

export function DashboardSyncStatus() {
  const navigate = useNavigate();
  // Shared source of truth — identical to the Pending Uploads screen.
  const { userActionable } = usePendingUploads();

  // Everything else is silent by design.
  if (!userActionable) return null;

  return (
    <button
      type="button"
      onClick={() => navigate("/pending-uploads")}
      className={cn(
        "mb-3 flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "border-destructive/30 bg-destructive/5 text-destructive"
      )}
      aria-label="Your device is low on storage. Tap for details."
    >
      <AlertTriangle className="h-4 w-4 flex-none" aria-hidden="true" />
      <span className="min-w-0 flex-1 whitespace-normal">
        <span className="block text-xs font-semibold leading-4 text-foreground">
          Action needed
        </span>
        <span className="block text-xs font-normal leading-4 text-muted-foreground">
          Your device is low on storage. Free up some space so photos can finish saving.
        </span>
      </span>
      <ChevronRight className="h-4 w-4 flex-none opacity-50" aria-hidden="true" />
    </button>
  );
}
